// Ein Tab pro Session (Audit M8a).
//
// Zwei Tabs mit demselben Projekt (persistKey) schrieben abwechselnd in
// denselben IndexedDB-Record — ein alter Tab vom Vortag überschrieb die
// Arbeit des neuen. Der Web-Locks-API-Lock `tro-vs-session:<persistKey>`
// gehört genau einem Tab; nur er darf speichern. Ein zweiter Tab sieht den
// Hinweis und kann den Lock übernehmen (`steal`), dann wird der erste Tab
// benachrichtigt (`onStolen`) und schaltet auf den Hinweis um.
//
// Chromium-only (Talent-Seite ist Chromium-only, BrowserGate). Fehlt die API
// trotzdem (Bypass-Flag), läuft alles ohne Lock wie bisher.

export interface TabLock {
  /** Lock freigeben (Unmount / persistKey-Wechsel). */
  release(): void;
  /** Wird gerufen, wenn ein anderer Tab den Lock übernommen hat. */
  onStolen(cb: () => void): void;
}

export function tabLockName(persistKey: string): string {
  return `tro-vs-session:${persistKey}`;
}

export function hasTabLocks(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function';
}

/** Versucht den Lock zu bekommen. `null` = ein anderer Tab hält ihn (nur
 *  ohne `steal`). Mit `steal` wird er dem anderen Tab entzogen. */
export function acquireTabLock(persistKey: string, steal = false): Promise<TabLock | null> {
  if (!hasTabLocks()) {
    return Promise.resolve({ release() {}, onStolen() {} });
  }
  return new Promise<TabLock | null>((resolve) => {
    let release: () => void = () => {};
    const held = new Promise<void>((r) => { release = r; });
    let stolenCb: (() => void) | null = null;
    let settled = false;
    const options: LockOptions = steal ? { steal: true } : { ifAvailable: true };
    navigator.locks
      .request(tabLockName(persistKey), options, (lock) => {
        if (!lock) {
          settled = true;
          resolve(null);
          return undefined;
        }
        settled = true;
        resolve({ release, onStolen(cb) { stolenCb = cb; } });
        return held;
      })
      .catch(() => {
        // `AbortError`: ein anderer Tab hat den Lock gestohlen — oder die
        // Anfrage scheiterte, bevor wir ihn hatten.
        if (!settled) {
          settled = true;
          resolve(null);
          return;
        }
        stolenCb?.();
      });
  });
}
